import { IBrowserInfo } from 'lib/helpers/browser-helper.class.js';
import { ISODate } from 'lib/time/index.js';
import { CosmosDb } from '../cosmosDb.js';
import { DietDb } from '../diet/dietDb.js';
import { WorkoutDb } from '../workout/workoutDb.js';

export namespace UserDb {
	export type UserLicense = 'free' | 'premium' | 'trialExpired';
	export type UserType = 'client' | 'trainer' | 'system';
	export type MeasurementUnitType = 'imperial' | 'metric';

	export interface IUserDietContainer {
		readonly id: string;
		readonly title: string;
		readonly items: IUserDietItem[];
	}

	export interface IUserDietItem {
		readonly id: string;
		diet: DietDb.IDietItem;
	}

	export interface IUserTrainingItem {
		readonly id: string;
		workout: WorkoutDb.IWorkoutItem;
	}

	export interface IUserTraining {
		readonly title: string;
		readonly containers: readonly IUserTrainingContainer[];
	}

	export interface IUserNutrition {
		readonly title: string;
		readonly containers: readonly IUserDietContainer[];
	}

	export interface IUserTrainingContainer {
		readonly id: string;
		readonly title: string;
		readonly items: IUserTrainingItem[];
	}

	export interface IUserAssignedProgressTypeValue {
		value: number;
		readonly date: ISODate;
	}

	export interface IUserAssignedProgressType {
		readonly id: string;
		readonly progressTypeId: string;
		readonly values: IUserAssignedProgressTypeValue[];
	}

	export interface IUserAssignedTask {
		readonly id: string;
		readonly taskId: string;
	}

	export interface IUserAssignedPackage {
		readonly id: string;
		readonly packageId: string;
	}

	export interface IUserSubscription {
		readonly subscriptionId: string;
		readonly cancelationUrl: string;
		readonly paymentUpdateUrl: string;
		readonly productId: string;
		readonly nextBilledDate: string;
		readonly status: string;
		readonly cancelationDate?: string;
	}

	export interface ILatestChatMessage {
		readonly message: string;
		readonly created: string;
	}

	export interface IUserBranding {
		readonly brandName: string;
		readonly brandBio?: string;
		readonly brandTel?: string;
		readonly brandingSubdomain?: string;
		readonly brandLogo?: CosmosDb.ICosmosFileReference[];
	}

	export interface IUserItem extends CosmosDb.ItemDefaultOmit<IBaseUserItem>, CosmosDb.ICosmosItem {
		avatar?: CosmosDb.ICosmosFileReferenceImage;
		fullName: string;
		privilege: CosmosDb.CosmosUserPrivilege;
	}

	export type UserGender = 'male' | 'female';

	export interface IBaseUserItem extends CosmosDb.IBaseItem {
		password: string;
		branding?: IUserBranding;
		email: string;
		readonly gender?: UserGender;
		readonly emailConfirmed?: boolean;
		readonly plainTextPassword?: string;
		readonly isDemoClient?: boolean;
		readonly isClientTemplate?: boolean;
		readonly isTrainerTemplate?: boolean;
		readonly isDeleted?: boolean;
		readonly latestChatMessageWithTrainer?: ILatestChatMessage;
		uploadedAvatar?: CosmosDb.ICosmosFileReference[];
		readonly userType: UserType;
		readonly license: UserLicense;
		readonly firstName?: string;
		readonly lastName?: string;
		readonly trainerId?: string;
		readonly birthDate?: string;
		readonly location?: string;
		readonly privateNotes?: string;
		readonly publicNotes?: string;
		readonly weightKg?: number;
		readonly heightCm?: number;
		readonly preferredUnit?: MeasurementUnitType;
		readonly lastActiveDate?: string;
		training?: IUserTraining;
		nutrition?: IUserNutrition;
		assignedProgressTypes?: readonly IUserAssignedProgressType[];
		clientFiles?: CosmosDb.ICosmosFileReference[];
		clientGalleryFiles?: CosmosDb.ICosmosFileReference[];
		assignedTasks?: readonly IUserAssignedTask[];
		assignedPackages?: readonly IUserAssignedPackage[];
		readonly subscription?: IUserSubscription;
		readonly timezone?: string;
		readonly unsubscribeFromEmails?: boolean;
		readonly lastLogonDate?: string;
		readonly browser?: IBrowserInfo;
		readonly referrer: string | undefined;
		readonly isDisabled: boolean | undefined;
	}

	export interface IUserIdProps {
		readonly email: string;
	}

	export interface IAuthUserResponse {
		readonly user: IUserItem;
		readonly token: string;
	}

	export interface IAuthUser {
		readonly id: string;
		readonly email: string;
		avatar: CosmosDb.ICosmosFileReferenceImage | undefined;
		readonly firstName?: string;
		readonly lastName?: string;
		readonly trainerId?: string;
		readonly license: UserLicense;
		readonly userType: UserType;
		readonly preferredUnit?: MeasurementUnitType;
		readonly privilege: CosmosDb.CosmosUserPrivilege;
		readonly created: string;
		readonly appVersion: string;
		readonly isDisabled: boolean;
	}

	export function isUserItem(item: CosmosDb.ICosmosItem): item is IUserItem {
		return item.system.type === 'User';
	}
}
